// Module: PublishCheckpointScreen
// Role: Keyed overview/editor for PUBLISH checkpoints. The overview only opens
//       the editor; the editor owns the artefact submission. State is keyed by
//       slot_id + checkpoint.id so a different checkpoint never inherits an open
//       editor or a late success from the previous one.
// Uses: PublishOverviewScreen, InstructionScreen, bricks/brick-registry,
//       PlatformContext
// Used by: FlowScreen.tsx (case 'PUBLISH' with multiple PUBLISH siblings)
// BUG-1589: the overview opens the proof form, never submit({artefact_path: ''}).

import { useCallback, useContext, useEffect, useState } from 'react';
import { PlatformContext } from '../../providers/PlatformProvider';
import { PublishOverviewScreen } from './templates/PublishOverviewScreen';
import { InstructionScreen } from './templates/InstructionScreen';
import { bricksForCheckpoint } from './bricks/brick-registry';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../api/flow-types';

export interface PublishCheckpointScreenProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  /** FlowScreen's submit handler — the overview itself never calls it. */
  onCta: () => void;
  /** Rejected/recoverable retry: open the form for the current checkpoint. */
  retrying?: boolean;
  /** Restore FlowScreen's back handler when the editor closes. */
  onBack?: () => void;
}

function isAccepted(attempts: CheckpointAttempt[], checkpointId: string): boolean {
  return attempts.some(
    (a) => a.checkpoint_id === checkpointId && a.verdict === 'accepted',
  );
}

/** The step PublishOverviewScreen marks active: first PUBLISH not yet accepted. */
function activePublishId(
  checkpoints: Checkpoint[],
  attempts: CheckpointAttempt[],
): string | null {
  const steps = checkpoints
    .filter((c) => c.kind === 'PUBLISH')
    .sort((a, b) => a.position - b.position);
  return steps.find((cp) => !isAccepted(attempts, cp.id))?.id ?? null;
}

export function PublishCheckpointScreen(props: PublishCheckpointScreenProps) {
  const stateKey = `${props.flowRun.slot_id}:${props.checkpoint.id}`;
  // Keyed remount: switching slot or checkpoint resets editor state and drops
  // any late response the previous checkpoint's editor still had in flight.
  return <PublishCheckpointState key={stateKey} {...props} />;
}

function PublishCheckpointState({
  checkpoint,
  attempt,
  flowRun,
  checkpoints,
  attempts,
  onCta,
  retrying = false,
  onBack,
}: PublishCheckpointScreenProps) {
  const platform = useContext(PlatformContext);
  const [editing, setEditing] = useState(retrying);

  useEffect(() => {
    if (retrying) setEditing(true);
  }, [retrying]);

  const closeEditor = useCallback(() => setEditing(false), []);

  useEffect(() => {
    if (!platform || !editing) return;
    platform.showBackButton(closeEditor);
    return () => {
      if (onBack) platform.showBackButton(onBack);
    };
  }, [platform, editing, closeEditor, onBack]);

  const templateProps = { checkpoint, attempt, flowRun, checkpoints, attempts, onCta };

  if (!editing) {
    const canOpen = activePublishId(checkpoints, attempts) === checkpoint.id;
    return (
      <PublishOverviewScreen
        {...templateProps}
        onCta={() => {
          // Server has not advanced to this step yet — the form stays closed.
          if (canOpen) setEditing(true);
        }}
      />
    );
  }

  const brick = bricksForCheckpoint(checkpoint)[0];
  if (!brick) return <InstructionScreen {...templateProps} />;

  const { key, Component } = brick;
  return <Component key={key} {...templateProps} />;
}
